"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

declare global {
  interface Window {
    GGBApplet?: new (
      params: Readonly<{
        appName: GeoGebraAppName;
        showToolBar: boolean;
        showAlgebraInput: boolean;
        showMenuBar: boolean;
        enableRightClick: boolean;
        enableShiftDragZoom: boolean;
        useBrowserForJS: boolean;
        scaleContainerClass: string;
        width: number;
        height: number;
      }>,
      version: string
    ) => { inject: (container: HTMLElement) => void };
    ggbApplet?: {
      setSize?: (w: number, h: number) => void;
      zoomIn?: () => void;
      zoomOut?: () => void;
      setStandardView?: () => void;
      reset?: () => void;
    } | null;
  }
}

type GeoGebraAppName = "geometry" | "graphing" | "3d" | "cas" | "suite";

const GEO_GEBRA_SCRIPT_SRC = "https://www.geogebra.org/apps/deployggb.js";

let geogebraLoaderPromise: Promise<void> | null = null;

function loadGeoGebraScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.GGBApplet) return Promise.resolve();
  if (geogebraLoaderPromise) return geogebraLoaderPromise;
  geogebraLoaderPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = GEO_GEBRA_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load GeoGebra script"));
    document.head.appendChild(script);
  });
  return geogebraLoaderPromise;
}

export default function GeoGebra() {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const appletRef = useRef<HTMLDivElement | null>(null);
  const [appName, setAppName] = useState<GeoGebraAppName>("geometry");
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showToolBar, setShowToolBar] = useState(true);
  const [showAlgebraInput] = useState(true);
  const reinjectTimerRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastSizeRef = useRef<{ w: number; h: number } | null>(null);

  // Load initial preferences from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem("ggb:prefs");
      if (raw) {
        const prefs = JSON.parse(raw) as {
          appName?: GeoGebraAppName;
          showToolBar?: boolean;
        };
        if (prefs.appName) setAppName(prefs.appName);
        if (typeof prefs.showToolBar === "boolean")
          setShowToolBar(prefs.showToolBar);
      }
    } catch {}
  }, []);

  // Persist preferences
  useEffect(() => {
    try {
      localStorage.setItem(
        "ggb:prefs",
        JSON.stringify({ appName, showToolBar })
      );
    } catch {}
  }, [appName, showToolBar]);

  const inject = useCallback(async () => {
    if (!wrapperRef.current || !appletRef.current) return;
    setIsReady(false);
    setError(null);
    await loadGeoGebraScript();
    if (!window.GGBApplet) return;

    const node = appletRef.current;
    // Clear any previous instance markup
    node.innerHTML = "";

    const width = wrapperRef.current.clientWidth || 800;
    const height = wrapperRef.current.clientHeight || 600;

    const params = {
      appName,
      showToolBar,
      showAlgebraInput,
      showMenuBar: false,
      enableRightClick: false,
      enableShiftDragZoom: true,
      useBrowserForJS: false,
      scaleContainerClass: "ggb-scale",
      width,
      height,
    } as const;

    try {
      // Per GeoGebra docs: new GGBApplet(parameters, '5.0')
      const Applet = window.GGBApplet;
      const applet = new Applet(params, "5.0");
      // Inject into our container
      applet.inject(node);
      setIsReady(true);
    } catch (e) {
      console.error("GeoGebra inject error", e);
      setError(
        e instanceof Error ? e.message : "Failed to initialize GeoGebra"
      );
    }
  }, [appName, showToolBar, showAlgebraInput]);

  useEffect(() => {
    inject();
  }, [inject]);

  // Resize handling: observe wrapper, throttle via rAF, prefer setSize; fallback to reinject
  useEffect(() => {
    if (!wrapperRef.current) return;
    const node = wrapperRef.current;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const cr = entry.contentRect;
      const w = Math.max(320, Math.floor(cr.width));
      const h = Math.max(240, Math.floor(cr.height));
      lastSizeRef.current = { w, h };
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        try {
          if (
            window.ggbApplet &&
            typeof window.ggbApplet.setSize === "function" &&
            lastSizeRef.current
          ) {
            window.ggbApplet.setSize(
              lastSizeRef.current.w,
              lastSizeRef.current.h
            );
          } else {
            if (reinjectTimerRef.current) {
              clearTimeout(reinjectTimerRef.current);
            }
            reinjectTimerRef.current = window.setTimeout(() => {
              inject();
            }, 150);
          }
        } catch {
          // ignore
        }
      });
    });
    ro.observe(node);
    return () => {
      ro.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (reinjectTimerRef.current) {
        clearTimeout(reinjectTimerRef.current);
        reinjectTimerRef.current = null;
      }
    };
  }, [inject]);

  // Keyboard shortcuts for quick actions (zoom/reset only)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!window.ggbApplet) return;
      if (e.key === "+" || (e.key === "+" && e.shiftKey)) {
        e.preventDefault();
        try {
          if (
            window.ggbApplet &&
            typeof window.ggbApplet.zoomIn === "function"
          ) {
            window.ggbApplet.zoomIn();
          }
        } catch {}
      } else if (e.key === "-") {
        e.preventDefault();
        try {
          if (
            window.ggbApplet &&
            typeof window.ggbApplet.zoomOut === "function"
          ) {
            window.ggbApplet.zoomOut();
          }
        } catch {}
      } else if (e.key.toLowerCase() === "r") {
        e.preventDefault();
        try {
          if (typeof window.ggbApplet.setStandardView === "function") {
            window.ggbApplet.setStandardView();
          } else if (typeof window.ggbApplet.reset === "function") {
            window.ggbApplet.reset();
          }
        } catch {}
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Grid/Axes toggles were removed from UI; no-op hooks deleted

  const options = useMemo(
    () =>
      [
        { id: "geometry", label: "Geometry" },
        { id: "graphing", label: "Graphing" },
        { id: "3d", label: "3D" },
        { id: "cas", label: "CAS" },
        { id: "suite", label: "Suite" },
      ] as Array<{ id: GeoGebraAppName; label: string }>,
    []
  );

  return (
    <div
      className="w-full h-full flex flex-col"
      role="region"
      aria-label="GeoGebra"
      aria-busy={!isReady}
    >
      <div className="px-3 py-2 border-b border-[--color-border] bg-[--color-card] flex items-center gap-2 select-none">
        <label className="text-sm text-muted-foreground" htmlFor="ggb-view">
          View
        </label>
        <select
          id="ggb-view"
          className="text-sm rounded-md border border-[--color-border] bg-background px-2 py-1"
          value={appName}
          onChange={(e) => setAppName(e.target.value as GeoGebraAppName)}
        >
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
        <div className="h-5 w-px bg-[--color-border] mx-1" aria-hidden="true" />
        <label className="inline-flex items-center gap-1 text-sm">
          <input
            type="checkbox"
            className="accent-[--color-accent]"
            checked={showToolBar}
            onChange={(e) => setShowToolBar(e.target.checked)}
          />
          Toolbar
        </label>
        <div className="ml-auto text-xs text-muted-foreground">
          {error ? (
            <span className="text-red-500">{error}</span>
          ) : isReady ? (
            "Ready"
          ) : (
            "Loading..."
          )}
        </div>
        {error && (
          <button
            className="ml-2 text-sm rounded-md border border-[--color-border] bg-background px-2 py-1"
            onClick={() => inject()}
          >
            Retry
          </button>
        )}
      </div>
      <div ref={wrapperRef} className="relative flex-1 min-h-0 ggb-scale">
        <div ref={appletRef} className="absolute inset-0" />
        {!isReady && !error && (
          <div className="absolute inset-0 grid place-items-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div
                className="h-4 w-4 rounded-full border-2 border-[--color-border] border-t-transparent animate-spin"
                aria-hidden="true"
              />
              Loading GeoGebra...
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
